import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import FetchStatus from '../../common/types/FetchStatus';
import apiService from '../../common/services/api/apiService';
import { AppThunk } from '../../configureStore';
import AlertType from '../../common/types/AlertType';
import { publicUrl } from 'env';

export type GroupReferenceKey = string;

type CartState = {
  meta: {
    fetchStatus: FetchStatus;
    message: string | null;
    messageType: string | null;
  };

  groupRefKeys: GroupReferenceKey[];
  orderSubtotal: string;
};

export const initialState: CartState = {
  meta: {
    fetchStatus: FetchStatus.none,
    message: null,
    messageType: null,
  },

  groupRefKeys: [],
  orderSubtotal: '0',
};

const CartSlice = createSlice({
  name: 'CartSlice',
  initialState,
  reducers: {
    fetchStart(state) {
      state.meta.message = null;
      state.meta.messageType = null;
      state.meta.fetchStatus = FetchStatus.loading;
      return state;
    },
    fetchFail(state, action: PayloadAction<{ message: string }>) {
      state.meta.fetchStatus = FetchStatus.loaded;
      state.meta.message = action.payload.message;
      state.meta.messageType = AlertType.danger;
      return state;
    },
    fetchComplete(state) {
      state.meta.fetchStatus = FetchStatus.loaded;
      state.meta.message = null;
      state.meta.messageType = null;
      return state;
    },

    addToCart(state, action: PayloadAction<GroupReferenceKey>) {
      state.groupRefKeys = [...state.groupRefKeys, action.payload];
      state.orderSubtotal = '';
      return state;
    },
    removeFromCart(state, action: PayloadAction<GroupReferenceKey>) {
      state.groupRefKeys = state.groupRefKeys.filter(
        (id) => id != action.payload
      );
      state.orderSubtotal = '';
      return state;
    },
    setOrderSubtotal(state, action: PayloadAction<string>) {
      state.orderSubtotal = action.payload;
      return state;
    },
    clearCart(state) {
      state.groupRefKeys = [];
      state.orderSubtotal = '';
      return state;
    },
  },
});

export function calculateAppOrderSubtotal(): AppThunk<Promise<void>> {
  return async function (dispatch, getState) {
    dispatch(CartSlice.actions.fetchStart());

    const {
      auth: { access_token },
      cart: { groupRefKeys },
    } = getState();

    const endpoint = `/checkout/calculateAppOrderSubtotal`;
    let error;

    try {
      const res = await apiService.post(endpoint, access_token, groupRefKeys);
      const txt = await res.text();
      if (res.ok) {
        dispatch(CartSlice.actions.setOrderSubtotal(txt));
        dispatch(CartSlice.actions.fetchComplete());
        return Promise.resolve();
      }
      error = txt;
    } catch (e) {
      error = (e instanceof Error && e.message.toString()) || '';
    }

    dispatch(CartSlice.actions.fetchFail({ message: error }));
    return Promise.reject(error);
  };
}

export function checkout(): AppThunk<Promise<void>> {
  return async function (dispatch, getState) {
    dispatch(CartSlice.actions.fetchStart());

    const {
      auth: { access_token },
      cart: { groupRefKeys },
    } = getState();

    const endpoint = `/checkout`;
    let error;

    try {
      // Just logging this in the event that the cart doesn't work
      // next build. (4/7/2021)
      console.info('(from cart) the public url is ' + publicUrl);
      const res = await apiService.post(endpoint, access_token, {
        groupRefKeys,
        returnUrl: publicUrl + '/order/confirmed',
        cancelUrl: publicUrl + '/order/canceled',
      });
      const json = await res.json();
      if (res.ok) {
        dispatch(CartSlice.actions.fetchComplete());
        location = json.redirectUrl;
        return Promise.resolve();
      }
      error = json;
    } catch (e) {
      error = (e instanceof Error && e.message.toString()) || '';
    }

    dispatch(CartSlice.actions.fetchFail({ message: error }));
    return Promise.reject(error);
  };
}

export function updateOrdersForProfile(): AppThunk<Promise<void>> {
  return async function (dispatch, getState) {
    dispatch(CartSlice.actions.fetchStart());

    const accessToken = getState().auth.access_token;
    const endpoint = `/checkout/updateOrdersForProfile`;
    let error;

    try {
      const res = await apiService.post(endpoint, accessToken, null);
      if (res.ok) {
        dispatch(CartSlice.actions.fetchComplete());
        return Promise.resolve();
      }
      error = await res.json();
    } catch (e) {
      error = (e instanceof Error && e.message.toString()) || '';
    }

    dispatch(CartSlice.actions.fetchFail({ message: error }));
    return Promise.reject(error);
  };
}

export const { addToCart, removeFromCart, clearCart } = CartSlice.actions;

export default CartSlice.reducer;
